// gkc_hash_code : 01H8DNTES58MRHRQY1CY91JDVE

export const PUBLIC_STATUS = {
  PUBLISH: "公開",
  PRIVATE: "非公開",
  LIMITED: "限定公開",
} as const;

export type PublicStatus = (typeof PUBLIC_STATUS)[keyof typeof PUBLIC_STATUS];

/*
 * ApplicationStatusEnumの日本語変換
 */
export const applicationStatus = {
  TEMPORARY_STORAGE: "一時保存",
  APPLICATION_ACCEPTANCE: "申込受付",
  APPLICATION_NG: "申込NG",
  SCREENING: "審査",
  APPRAISAL_NG: "審査NG",
  CONTRACT: "契約",
  CONTRACT_NG: "契約NG",
  CONTRACT_COMPLETED: "契約中",
  CANCELLATION_ACCEPTANCE: "解約受付",
  CANCELLATION_SCHEDULED: "解約予定",
  CONTRACT_FORCE_CANCELED: "強制解約",
  CONTRACT_NORMAL_CANCELED: "通常解約",
} as const;

export const applicationStatusNumber = {
  TEMPORARY_STORAGE: 0,
  APPLICATION_ACCEPTANCE: 1,
  APPLICATION_NG: 2,
  SCREENING: 3,
  APPRAISAL_NG: 4,
  CONTRACT: 5,
  CONTRACT_NG: 6,
  CONTRACT_COMPLETED: 7,
  CANCELLATION_ACCEPTANCE: 8,
  CANCELLATION_SCHEDULED: 9,
  CONTRACT_FORCE_CANCELED: 10,
  CONTRACT_NORMAL_CANCELED: 11,
} as const;

// 逆引き用のMapを作成
export const reversedApplicationStatus = Object.fromEntries(
  Object.entries(applicationStatus).map(([key, value]) => [value, key])
);

export type ApplicationStatus = keyof typeof applicationStatus;

export type ApplicationStatusValue = (typeof applicationStatus)[ApplicationStatus];

export type ApplicationStatusNumber = keyof typeof applicationStatusNumber;

export type ApplicationStatusNumberValue = (typeof applicationStatusNumber)[ApplicationStatus];

export const applicationStatusByNumber = (statusNumber: ApplicationStatusNumberValue): ApplicationStatusValue => {
  if (statusNumber === 0) return applicationStatus.TEMPORARY_STORAGE;
  if (statusNumber === 1) return applicationStatus.APPLICATION_ACCEPTANCE;
  if (statusNumber === 2) return applicationStatus.APPLICATION_NG;
  if (statusNumber === 3) return applicationStatus.SCREENING;
  if (statusNumber === 4) return applicationStatus.APPRAISAL_NG;
  if (statusNumber === 5) return applicationStatus.CONTRACT;
  if (statusNumber === 6) return applicationStatus.CONTRACT_NG;
  if (statusNumber === 7) return applicationStatus.CONTRACT_COMPLETED;
  if (statusNumber === 8) return applicationStatus.CANCELLATION_ACCEPTANCE;
  if (statusNumber === 9) return applicationStatus.CANCELLATION_SCHEDULED;
  if (statusNumber === 10) return applicationStatus.CONTRACT_FORCE_CANCELED;
  if (statusNumber === 11) return applicationStatus.CONTRACT_NORMAL_CANCELED;
  return applicationStatus.TEMPORARY_STORAGE;
};

export const applicationStatusByEnStatus = (status: ApplicationStatus): ApplicationStatusNumberValue | undefined => {
  if (status === "TEMPORARY_STORAGE") return applicationStatusNumber.TEMPORARY_STORAGE;
  if (status === "APPLICATION_ACCEPTANCE") return applicationStatusNumber.APPLICATION_ACCEPTANCE;
  if (status === "APPLICATION_NG") return applicationStatusNumber.APPLICATION_NG;
  if (status === "SCREENING") return applicationStatusNumber.SCREENING;
  if (status === "APPRAISAL_NG") return applicationStatusNumber.APPRAISAL_NG;
  if (status === "CONTRACT") return applicationStatusNumber.CONTRACT;
  if (status === "CONTRACT_NG") return applicationStatusNumber.CONTRACT_NG;
  if (status === "CONTRACT_COMPLETED") return applicationStatusNumber.CONTRACT_COMPLETED;
  if (status === "CANCELLATION_ACCEPTANCE") return applicationStatusNumber.CANCELLATION_ACCEPTANCE;
  if (status === "CANCELLATION_SCHEDULED") return applicationStatusNumber.CANCELLATION_SCHEDULED;
  if (status === "CONTRACT_FORCE_CANCELED") return applicationStatusNumber.CONTRACT_FORCE_CANCELED;
  if (status === "CONTRACT_NORMAL_CANCELED") return applicationStatusNumber.CONTRACT_NORMAL_CANCELED;
  return undefined;
};

export const INVALID_APPLICATION_STATUS_FOR_RENTAL_INFORMATION = [
  applicationStatusNumber.TEMPORARY_STORAGE,
  applicationStatusNumber.APPLICATION_ACCEPTANCE,
  applicationStatusNumber.APPLICATION_NG,
  applicationStatusNumber.SCREENING,
  applicationStatusNumber.APPRAISAL_NG,
  applicationStatusNumber.CONTRACT,
  applicationStatusNumber.CONTRACT_NG,
] as ApplicationStatusNumberValue[];

export const APPLICATION_STATUS_FOR_RENTAL_INFORMATION = [
  applicationStatusNumber.CONTRACT,
  applicationStatusNumber.CONTRACT_NG,
  applicationStatusNumber.CONTRACT_COMPLETED,
  applicationStatusNumber.CANCELLATION_ACCEPTANCE,
  applicationStatusNumber.CANCELLATION_SCHEDULED,
  applicationStatusNumber.CONTRACT_FORCE_CANCELED,
  applicationStatusNumber.CONTRACT_NORMAL_CANCELED,
] as ApplicationStatusNumberValue[];

/**
 * Timestamps for contract step progression
 */
export type StepTimestamps = {
  /** Initial payment completed timestamp */
  initial_payment_at?: string | null;
  /** Lessor signed contract document timestamp */
  lessor_sign_contract_document_at?: string | null;
  /** Lessee signed contract document timestamp */
  lessee_sign_contract_document_at?: string | null;
  /** Final confirmation of signed contract timestamp */
  final_confirm_sign_contract_document_at?: string | null;
  /** Contract creation timestamp */
  contract_creation_at?: string | null;
  /** Contract signed and uploaded timestamp */
  sign_and_upload_at?: string | null;
  /** Bank transfer setting completed timestamp */
  bank_transfer_setting_at?: string | null;
};

/**
 * Timestamps for confirmation step progression
 */
export type ConfirmationStepTimestamps = {
  /** Contract content confirmed timestamp */
  confirm_contract_content_at?: string | null;
  /** Contract document created timestamp */
  create_contract_document_at?: string | null;
  /** Contract confirmed and stamp requested timestamp */
  confirm_and_request_stamp_at?: string | null;
};

/**
 * Additional information for application/contract
 */
export type AdditionalInfo = {
  /** Step progression timestamps */
  step_timestamps?: StepTimestamps;
  /** Confirmation step progression timestamps */
  confirmation_step_timestamps?: ConfirmationStepTimestamps;
};
